'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  AutocompleteSection,
  Avatar,
  Chip,
  Button,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DateRangePicker,
} from '@heroui/react';
import {
  FaSearch,
  FaMapMarkerAlt,
  FaFileSignature,
  FaTimes,
  FaCalendarAlt,
} from 'react-icons/fa';
import { BsStars } from 'react-icons/bs';
import debounce from 'lodash/debounce';
import { CalendarDate } from '@internationalized/date';
import useServerInfiniteSearch from '@/util/hooks/useServerInfiniteSearch';
import { Inspection } from '@/util/types/serverTypeCollection/inspection';
import { Quote } from '@/util/types/serverTypeCollection/quote';
import { ContactType } from '@/util/types/serverTypeCollection/contact';
import { useTZDayjs } from '@/context/TimezoneContext';
import { getInspectionDisplayAddress } from '@/util/inspection/getInspectionDisplayAddress';
import { SessionType } from '@/util/types/globalTypes';
import AISearchInput from './AISearchInput';
import {
  detectGlobalSearchDateKeyword,
  keywordToRange,
  formatGlobalSearchDateKeywordLabel,
  type GlobalSearchDateKeyword,
} from '@/util/search/parseGlobalSearchDateKeywords';

type EntityKey = 'inspection' | 'quote' | 'contact';
type EntityFilters = Record<EntityKey, boolean>;
const ALL_ENABLED: EntityFilters = { inspection: true, quote: true, contact: true };

const PAGE_SIZE = 10;

/** Scrollable listbox cap; actual height also clamped to viewport (see listboxMaxHeight state). */
const LISTBOX_HEIGHT_CAP = 760;
/** Space for navbar, search trigger, filter row, popover padding, and safe margin. */
const LISTBOX_VIEWPORT_RESERVE = 220;

const GLOBAL_SEARCH_DATE_PRESETS: { label: string; keyword: GlobalSearchDateKeyword }[] = [
  { label: 'Today', keyword: 'today' },
  { label: 'Yesterday', keyword: 'yesterday' },
  { label: 'Tomorrow', keyword: 'tomorrow' },
  { label: 'This week', keyword: 'thisweek' },
  { label: 'Last week', keyword: 'lastweek' },
  { label: 'Next week', keyword: 'nextweek' },
  { label: 'This month', keyword: 'thismonth' },
  { label: 'Past', keyword: 'past' },
  { label: 'Future', keyword: 'future' },
];

type DateFilterState = {
  after?: string;
  before?: string;
  label: string;
  keyword?: GlobalSearchDateKeyword | 'custom';
};

function isLikelyAddress(term: string): boolean {
  const addressPatterns = [
    /\d+\s+\w+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|pl|place|way|cir|circle)/i,
    /\d+\s+\w+/,
    /\w+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|pl|place|way|cir|circle)/i,
  ];
  return addressPatterns.some((pattern) => pattern.test(term));
}

interface GlobalSearchProps {
  className?: string;
  onItemSelect?: () => void;
  session: SessionType;
}

interface SearchItem {
  id: string;
  type: EntityKey;
  data: Inspection | Quote | ContactType;
  label: string;
  description: string;
}

type SearchPayload = {
  text: string;
  after?: string;
  before?: string;
};

export default function GlobalSearch({ className, onItemSelect, session }: GlobalSearchProps) {
  const tzDayjs = useTZDayjs();
  const [inputValue, setInputValue] = useState('');
  const [searchPayload, setSearchPayload] = useState<SearchPayload | null>(null);
  const [showAIMode, setShowAIMode] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [entityFilters, setEntityFilters] = useState<EntityFilters>(ALL_ENABLED);
  const [dateFilter, setDateFilter] = useState<DateFilterState | null>(null);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [customRangeValue, setCustomRangeValue] = useState<{
    start: CalendarDate;
    end: CalendarDate;
  } | null>(null);
  const [pickerMounted, setPickerMounted] = useState(false);
  const [listboxMaxHeight, setListboxMaxHeight] = useState(LISTBOX_HEIGHT_CAP);

  const scrollNearBottomGuard = useRef(0);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const sync = () => {
      const raw = window.innerHeight - LISTBOX_VIEWPORT_RESERVE;
      setListboxMaxHeight(Math.min(LISTBOX_HEIGHT_CAP, Math.max(0, raw)));
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  useEffect(() => {
    setPickerMounted(true);
  }, []);

  const debouncedSetPayload = useMemo(
    () => debounce((payload: SearchPayload | null) => setSearchPayload(payload), 300),
    [],
  );
  useEffect(() => () => debouncedSetPayload.cancel(), []);

  const detectedKeyword = useMemo(() => detectGlobalSearchDateKeyword(inputValue), [inputValue]);

  const hasActiveSearch = Boolean(
    dateFilter ||
      (searchPayload &&
        (searchPayload.text.trim().length > 2 ||
          !!searchPayload.after ||
          !!searchPayload.before)),
  );

  useEffect(() => {
    const hasText = detectedKeyword.textQuery.length > 2;
    const hasKeyword = !!detectedKeyword.matchedKeyword;
    if (!hasText && !hasKeyword) {
      debouncedSetPayload.cancel();
      setSearchPayload(null);
      return;
    }
    const range = detectedKeyword.matchedKeyword
      ? keywordToRange(detectedKeyword.matchedKeyword, tzDayjs())
      : null;
    debouncedSetPayload({
      text: detectedKeyword.textQuery,
      after: range?.after,
      before: range?.before,
    });
  }, [detectedKeyword, debouncedSetPayload, tzDayjs]);

  const effectiveAfter = dateFilter?.after ?? searchPayload?.after;
  const effectiveBefore = dateFilter?.before ?? searchPayload?.before;
  const searchText = searchPayload?.text?.trim() ?? '';

  const shouldSearchContacts =
    hasActiveSearch &&
    entityFilters.contact &&
    !isLikelyAddress(searchText) &&
    searchText.length > 2;

  const inspectionArgs = useMemo(() => {
    if (!hasActiveSearch || !entityFilters.inspection) return null;
    return {
      url: 'inspection',
      params: {
        ...(searchText ? { search: searchText } : {}),
        ...(effectiveAfter ? { after: effectiveAfter } : {}),
        ...(effectiveBefore ? { before: effectiveBefore } : {}),
      },
    };
  }, [hasActiveSearch, entityFilters.inspection, searchText, effectiveAfter, effectiveBefore]);

  const quoteArgs = useMemo(() => {
    if (!hasActiveSearch || !entityFilters.quote) return null;
    return {
      url: 'quote',
      params: {
        ...(searchText ? { search: searchText } : {}),
        ...(effectiveAfter ? { after: effectiveAfter } : {}),
        ...(effectiveBefore ? { before: effectiveBefore } : {}),
      },
    };
  }, [hasActiveSearch, entityFilters.quote, searchText, effectiveAfter, effectiveBefore]);

  const contactArgs = useMemo(() => {
    if (!shouldSearchContacts) return null;
    return {
      url: 'contact',
      params: { search: searchText },
    };
  }, [shouldSearchContacts, searchText]);

  const inspSearch = useServerInfiniteSearch<Inspection>(inspectionArgs, PAGE_SIZE);
  const quoteSearch = useServerInfiniteSearch<Quote>(quoteArgs, PAGE_SIZE);
  const contactSearch = useServerInfiniteSearch<ContactType>(contactArgs, PAGE_SIZE);

  const inspections = hasActiveSearch && entityFilters.inspection ? inspSearch.items : [];
  const quotes = hasActiveSearch && entityFilters.quote ? quoteSearch.items : [];
  const contacts = hasActiveSearch && entityFilters.contact ? contactSearch.items : [];

  const handleNavigation = (type: EntityKey, item: any) => {
    switch (type) {
      case 'inspection':
        window.open(`/inspections/${item._id}`, '_blank');
        break;
      case 'quote':
        window.open(`/work/quotes?quoteId=${item._id}`, '_blank');
        break;
      case 'contact':
        window.open(`/contacts/${item._id}`, '_blank');
        break;
    }
  };

  const formatServices = (services: string): string => {
    if (!services) return 'No services listed';
    return services.length > 26 ? `${services.substring(0, 26)}...` : services;
  };

  const formatPeopleNames = (people: any[]): string => {
    if (!people || people.length === 0) return 'No contacts';
    return (
      people
        .map((p) => {
          const c = p._contactId;
          return c?.firstName && c?.lastName ? `${c.firstName} ${c.lastName}` : c?.email || 'Unknown';
        })
        .slice(0, 2)
        .join(', ') + (people.length > 2 ? ` +${people.length - 2} more` : '')
    );
  };

  const chipColor = (
    status: string,
    map: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'>,
  ) => map[status] ?? 'default';

  const quoteStatusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
    accepted: 'success',
    rejected: 'danger',
    pending: 'warning',
    draft: 'default',
    archived: 'secondary',
    on_hold: 'secondary',
  };
  const inspectionStatusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> =
    {
      confirmed: 'success',
      unconfirmed: 'warning',
      cancelled: 'danger',
    };

  const { inspectionItems, quoteItems, contactItems } = useMemo(() => {
    const inspectionItems: SearchItem[] = [];
    const quoteItems: SearchItem[] = [];
    const contactItems: SearchItem[] = [];

    if (inspections && Array.isArray(inspections)) {
      inspections.forEach((insp) => {
        inspectionItems.push({
          id: `inspection-${insp._id}`,
          type: 'inspection',
          data: insp,
          label: getInspectionDisplayAddress(insp) || 'Unknown address',
          description: `${insp.datetime ? tzDayjs(insp.datetime).format('M/D/YY h:mmA') : 'Unknown date'} • ${formatServices(insp.services)} • ${formatPeopleNames(insp.people || [])}`,
        });
      });
    }
    if (quotes && Array.isArray(quotes)) {
      quotes.forEach((q) => {
        const addr = q.property?.street
          ? `${q.property.street}, ${q.property.city}, ${q.property.state}`
          : 'Address not available';
        quoteItems.push({
          id: `quote-${q._id}`,
          type: 'quote',
          data: q,
          label: addr,
          description: `${q.createdAt ? tzDayjs(q.createdAt).format('M/D/YY') : 'Date unknown'} • ${formatPeopleNames(q.people || [])}`,
        });
      });
    }
    if (contacts && Array.isArray(contacts) && shouldSearchContacts) {
      contacts.forEach((c) => {
        const name =
          c.firstName || c.lastName
            ? `${c.firstName || ''} ${c.lastName || ''}`.trim()
            : c.email || 'Unnamed Contact';
        contactItems.push({
          id: `contact-${c._id}`,
          type: 'contact',
          data: c,
          label: name,
          description: c.email || '',
        });
      });
    }
    return { inspectionItems, quoteItems, contactItems };
  }, [inspections, quotes, contacts, shouldSearchContacts, tzDayjs]);

  const showSpinner = Boolean(
    hasActiveSearch &&
      ((entityFilters.inspection && inspSearch.isLoading && inspSearch.items.length === 0) ||
        (entityFilters.quote && quoteSearch.isLoading && quoteSearch.items.length === 0) ||
        (entityFilters.contact && contactArgs && contactSearch.isLoading && contactSearch.items.length === 0)),
  );

  /** Infinite scroll only when exactly one entity type is selected (avoids loading 3 streams at once). */
  const allowInfiniteScroll =
    [entityFilters.inspection, entityFilters.quote, entityFilters.contact].filter(Boolean).length === 1;

  const isValidatingAny =
    allowInfiniteScroll &&
    ((entityFilters.inspection && inspSearch.isLoadingMore) ||
      (entityFilters.quote && quoteSearch.isLoadingMore) ||
      (entityFilters.contact && contactArgs && contactSearch.isLoadingMore));

  const hasResults = inspectionItems.length > 0 || quoteItems.length > 0 || contactItems.length > 0;

  const loadMoreAll = useCallback(() => {
    if (!allowInfiniteScroll) return;
    if (inspSearch.hasMore) inspSearch.loadMore();
    if (quoteSearch.hasMore) quoteSearch.loadMore();
    if (contactArgs && contactSearch.hasMore) contactSearch.loadMore();
  }, [
    allowInfiniteScroll,
    inspSearch.hasMore,
    inspSearch.loadMore,
    quoteSearch.hasMore,
    quoteSearch.loadMore,
    contactArgs,
    contactSearch.hasMore,
    contactSearch.loadMore,
  ]);

  const handleScrollShadowScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      if (!allowInfiniteScroll) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
      const now = Date.now();
      if (now - scrollNearBottomGuard.current < 400) return;
      scrollNearBottomGuard.current = now;
      loadMoreAll();
    },
    [allowInfiniteScroll, loadMoreAll],
  );

  const toggleEntity = (key: EntityKey) => {
    setEntityFilters((prev) => {
      const allOn = prev.inspection && prev.quote && prev.contact;
      if (allOn) {
        return {
          inspection: key === 'inspection',
          quote: key === 'quote',
          contact: key === 'contact',
        };
      }
      const next = { ...prev, [key]: !prev[key] };
      if (!next.inspection && !next.quote && !next.contact) return ALL_ENABLED;
      return next;
    });
  };

  const clearDateKeyword = () => {
    if (!detectedKeyword.matchedKeyword) return;
    const tokens = inputValue.trim().split(/\s+/);
    setInputValue(
      tokens.filter((t) => t.toLowerCase() !== detectedKeyword.matchedKeyword!.toLowerCase()).join(' '),
    );
  };

  const clearAll = () => {
    setInputValue('');
    setEntityFilters(ALL_ENABLED);
    setDateFilter(null);
    setCustomRangeValue(null);
  };

  const applyPreset = (keyword: GlobalSearchDateKeyword) => {
    const range = keywordToRange(keyword, tzDayjs());
    setCustomRangeValue(null);
    setDateFilter({
      ...range,
      label: formatGlobalSearchDateKeywordLabel(keyword),
      keyword,
    });
    setIsDatePopoverOpen(false);
  };

  const handleCustomRangeChange = (val: { start: CalendarDate; end: CalendarDate } | null) => {
    if (!val?.start || !val?.end) {
      setCustomRangeValue(null);
      return;
    }
    setCustomRangeValue(val);
    const from = new Date(val.start.year, val.start.month - 1, val.start.day);
    const to = new Date(val.end.year, val.end.month - 1, val.end.day);
    setDateFilter({
      after: tzDayjs(from).startOf('day').toISOString(),
      before: tzDayjs(to).endOf('day').toISOString(),
      label: 'Custom range',
      keyword: 'custom',
    });
  };

  const allActive = entityFilters.inspection && entityFilters.quote && entityFilters.contact;
  const hasFiltersOrInput = inputValue.length > 0 || !allActive || !!dateFilter;

  const listboxEmptyMessage = (): string => {
    if (!hasActiveSearch) return 'Type to search or use date & entity filters';
    if (showSpinner) return 'Searching…';
    return 'No results found';
  };

  const filterBar = (
    <div className='flex w-full flex-wrap items-center gap-1 px-1 pb-1'>
      {(['inspection', 'quote', 'contact'] as EntityKey[]).map((key) => (
        <Button
          key={key}
          size='sm'
          variant={entityFilters[key] ? 'solid' : 'bordered'}
          color={entityFilters[key] ? 'primary' : 'default'}
          className='h-6 min-w-0 px-2 text-[11px] font-bold uppercase'
          onPress={() => toggleEntity(key)}
        >
          {key === 'inspection' ? 'Inspections' : key === 'quote' ? 'Quotes' : 'Contacts'}
        </Button>
      ))}
      <Popover isOpen={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen} placement='bottom-start'>
        <PopoverTrigger>
          <Button
            size='sm'
            variant={dateFilter ? 'solid' : 'bordered'}
            color={dateFilter ? 'success' : 'default'}
            className='h-6 min-w-0 px-2'
            aria-label='Filter by date'
            isIconOnly
          >
            <FaCalendarAlt size={12} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[min(100vw-2rem,320px)] p-3 lg:max-w-2xl xl:max-w-3xl'>
          <p className='text-[10px] uppercase text-gray-500 mb-2'>Date presets</p>
          <div className='flex flex-wrap gap-1 mb-3'>
            {GLOBAL_SEARCH_DATE_PRESETS.map(({ label, keyword }) => (
              <Button
                key={keyword}
                size='sm'
                variant='flat'
                className='h-7 min-w-0 px-2 text-[11px]'
                onPress={() => applyPreset(keyword)}
              >
                {label}
              </Button>
            ))}
          </div>
          {pickerMounted ? (
            <>
              <p className='text-[10px] uppercase text-gray-500 mb-2'>Custom range</p>
              <DateRangePicker
                aria-label='Custom date range'
                value={customRangeValue}
                onChange={handleCustomRangeChange}
                visibleMonths={1}
                size='sm'
                variant='bordered'
                classNames={{
                  base: 'w-full',
                  inputWrapper: 'bg-white shadow-none border min-h-9',
                }}
              />
            </>
          ) : (
            <div className='h-10 rounded-lg border border-default-200 bg-default-100' aria-hidden />
          )}
        </PopoverContent>
      </Popover>
      {dateFilter ? (
        <Chip
          size='sm'
          variant='flat'
          color='success'
          onClose={() => {
            setDateFilter(null);
            setCustomRangeValue(null);
          }}
          classNames={{ closeButton: 'text-foreground' }}
        >
          {dateFilter.label}
        </Chip>
      ) : null}
      {!dateFilter && detectedKeyword.matchedKeyword ? (
        <Chip
          size='sm'
          variant='flat'
          color='success'
          onClose={clearDateKeyword}
          classNames={{ closeButton: 'text-foreground' }}
        >
          {formatGlobalSearchDateKeywordLabel(detectedKeyword.matchedKeyword)}
        </Chip>
      ) : null}
      {isValidatingAny && hasResults ? (
        <span className='text-[10px] text-gray-400 uppercase tracking-wide ml-auto'>Loading more…</span>
      ) : null}
    </div>
  );

  const bottomListContent =
    hasActiveSearch && hasResults && isValidatingAny ? (
      <div className='px-2 py-1 text-center text-[10px] text-gray-400'>Loading more results…</div>
    ) : null;

  if (!isMounted) {
    return (
      <div className={className}>
        <div className='flex items-center gap-2'>
          <div className='flex h-10 w-full max-w-[min(100vw-1rem,28rem)] items-center rounded-full border border-gray-300 bg-white px-4 md:max-w-md lg:max-w-2xl xl:max-w-3xl'>
            <FaSearch className='text-gray-400' size={16} />
            <input
              className='w-full bg-transparent outline-none ml-2 text-sm'
              placeholder='Search inspections, quotes, contacts...'
              disabled
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {showAIMode && session.user._companyId === '624c9327e90fc4025d8f586b' ? (
        <AISearchInput
          className='w-full max-w-[min(100vw-1rem,28rem)] md:max-w-md lg:max-w-2xl xl:max-w-3xl'
          onBack={() => setShowAIMode(false)}
          session={session}
        />
      ) : (
        <div className='flex items-center gap-2'>
          <Autocomplete
            placeholder='Search inspections, quotes, contacts...'
            inputValue={inputValue}
            onInputChange={setInputValue}
            isLoading={showSpinner}
            startContent={<FaSearch className='text-gray-400' size={16} />}
            endContent={
              hasFiltersOrInput ? (
                <Tooltip
                  content={
                    !allActive && inputValue.length > 0
                      ? 'Clear search & filters'
                      : !allActive
                        ? 'Reset filters'
                        : 'Clear search'
                  }
                  placement='bottom'
                  color='foreground'
                  closeDelay={0}
                >
                  <button
                    className='text-gray-400 hover:text-foreground transition-colors outline-none'
                    onClick={clearAll}
                    aria-label='Clear search and filters'
                  >
                    <FaTimes size={12} />
                  </button>
                </Tooltip>
              ) : undefined
            }
            aria-label='Global search for inspections, quotes, and contacts'
            allowsCustomValue={true}
            allowsEmptyCollection={true}
            disabledKeys={
              hasActiveSearch && !hasResults && !showSpinner ? ['__empty__'] : undefined
            }
            menuTrigger='focus'
            disableAnimation={true}
            shouldCloseOnBlur={false}
            popoverProps={{ shouldCloseOnScroll: false }}
            selectedKey={null}
            defaultFilter={() => true}
            scrollShadowProps={{
              onScroll: handleScrollShadowScroll,
            }}
            classNames={{
              base: 'w-full max-w-[min(100vw-1rem,28rem)] md:max-w-md lg:max-w-2xl xl:max-w-3xl',
              selectorButton: 'hidden',
            }}
            inputProps={{
              classNames: {
                inputWrapper: 'shadow-none bg-white border',
                input: 'text-sm',
              },
            }}
            listboxProps={{
              // HeroUI ties hideEmptyContent to allowsCustomValue; when both are true the popover
              // never opens with an empty collection (no typing yet). We need the menu open on
              // focus so filters are usable without typing.
              hideEmptyContent: false,
              topContent: filterBar,
              bottomContent: bottomListContent,
              emptyContent: listboxEmptyMessage(),
            }}
            onSelectionChange={(key) => {
              if (key) {
                const allItems = [...inspectionItems, ...quoteItems, ...contactItems];
                const item = allItems.find((i) => i.id === key);
                if (item) {
                  handleNavigation(item.type, item.data);
                  setInputValue('');
                  onItemSelect?.();
                }
              }
            }}
            maxListboxHeight={listboxMaxHeight}
            size='md'
            variant='bordered'
            radius='full'
          >
            {inspectionItems.length > 0 ? (
              <AutocompleteSection
                key='inspections'
                title='Inspections'
                classNames={{ heading: 'text-sm font-semibold text-gray-700' }}
              >
                {inspectionItems.map((item) => (
                  <AutocompleteItem
                    key={item.id}
                    textValue={`inspection-${item.id}-${item.label}`}
                    classNames={{ base: 'min-w-0 items-start' }}
                    startContent={<FaMapMarkerAlt className='mt-0.5 shrink-0 text-red-500' size={16} />}
                  >
                    <div className='grid w-full min-w-0 grid-cols-1 gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-2'>
                      <div className='flex min-w-0 flex-col'>
                        <span className='text-sm font-medium wrap-break-word'>{item.label}</span>
                        {item.description && (
                          <span className='text-xs text-gray-500 wrap-break-word'>{item.description}</span>
                        )}
                      </div>
                      <Chip
                        size='sm'
                        color={chipColor((item.data as Inspection).status, inspectionStatusColors)}
                        className='h-auto min-h-4 w-fit max-w-full justify-self-start py-0.5 text-[10px] font-bold! uppercase sm:justify-self-end'
                        classNames={{ content: 'wrap-break-word font-bold!' }}
                      >
                        {(item.data as Inspection).status}
                      </Chip>
                    </div>
                  </AutocompleteItem>
                ))}
              </AutocompleteSection>
            ) : null}

            {quoteItems.length > 0 ? (
              <AutocompleteSection
                key='quotes'
                title='Quotes'
                classNames={{ heading: 'text-sm font-semibold text-gray-700' }}
              >
                {quoteItems.map((item) => (
                  <AutocompleteItem
                    key={item.id}
                    textValue={`quote-${item.id}-${item.label}`}
                    classNames={{ base: 'min-w-0 items-start' }}
                    startContent={<FaFileSignature className='mt-0.5 shrink-0 text-warning' size={16} />}
                  >
                    <div className='grid w-full min-w-0 grid-cols-1 gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-2'>
                      <div className='flex min-w-0 flex-col'>
                        <span className='text-sm font-medium wrap-break-word'>{item.label}</span>
                        {item.description && (
                          <span className='text-xs text-gray-500 wrap-break-word'>{item.description}</span>
                        )}
                      </div>
                      <Chip
                        size='sm'
                        color={chipColor((item.data as Quote).status, quoteStatusColors)}
                        className='h-auto min-h-4 w-fit max-w-full justify-self-start py-0.5 text-[10px] font-bold! uppercase sm:justify-self-end'
                        classNames={{ content: 'wrap-break-word font-bold!' }}
                      >
                        {(item.data as Quote).status.replace(/_/g, ' ')}
                      </Chip>
                    </div>
                  </AutocompleteItem>
                ))}
              </AutocompleteSection>
            ) : null}

            {contactItems.length > 0 && shouldSearchContacts ? (
              <AutocompleteSection
                key='contacts'
                title='Contacts'
                classNames={{ heading: 'text-sm font-semibold text-gray-700' }}
              >
                {contactItems.map((item) => (
                  <AutocompleteItem
                    key={item.id}
                    textValue={`contact-${item.id}-${item.label}`}
                    startContent={
                      <Avatar
                        size='sm'
                        src={(item.data as ContactType).thumb || (item.data as ContactType).image}
                        name={item.label}
                        className='shrink-0'
                      />
                    }
                  >
                    <div className='flex flex-col'>
                      <span className='text-sm font-medium'>{item.label}</span>
                      {item.description && <span className='text-xs text-gray-500'>{item.description}</span>}
                    </div>
                  </AutocompleteItem>
                ))}
              </AutocompleteSection>
            ) : null}

            {hasActiveSearch && !hasResults && !showSpinner ? (
              <AutocompleteItem
                key='__empty__'
                textValue='__empty__'
                className='opacity-100 cursor-default data-[hover=true]:bg-transparent'
              >
                <span className='text-sm text-gray-500'>{listboxEmptyMessage()}</span>
              </AutocompleteItem>
            ) : null}
          </Autocomplete>

          {session.user._companyId === '624c9327e90fc4025d8f586b' && (
            <Tooltip content='AI Assistant' placement='bottom' color='foreground' closeDelay={50} showArrow>
              <Button
                isIconOnly
                variant='shadow'
                radius='full'
                color='primary'
                size='sm'
                onPress={() => setShowAIMode(true)}
                aria-label='AI Assistant'
                className='shrink-0'
              >
                <BsStars className='' size={16} />
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
